import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { reportsData } from '../data/reports';
import ComparisonRadarChart from '../components/ComparisonRadarChart';

const { FiArrowLeft, FiPlus, FiX, FiTrendingUp, FiCheck, FiInfo, FiActivity } = FiIcons;

const ComparisonTool = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const reports = Object.values(reportsData);

  const toggleSelection = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else if (selectedIds.length < 3) {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const selectedReports = selectedIds.map(id => reportsData[id]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <Link to="/dashboard" className="text-gray-600 hover:text-blue-600 flex items-center mb-4 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Dashboard
          </Link>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Assessment Comparison</h1>
              <p className="text-gray-600">Select up to 3 assessments to compare performance and track progress.</p>
            </div>
            <div className="flex gap-2">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                {selectedIds.length} / 3 Selected
              </span>
              {selectedIds.length > 0 && (
                <button 
                  onClick={() => setSelectedIds([])}
                  className="text-gray-500 hover:text-red-600 text-sm font-medium"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        </header>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar: Selection List */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider px-1">Available Reports</h3>
            <div className="space-y-3">
              {reports.map((report) => {
                const isSelected = selectedIds.includes(report.id);
                return (
                  <button
                    key={report.id}
                    onClick={() => toggleSelection(report.id)}
                    disabled={!isSelected && selectedIds.length >= 3}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50 shadow-sm' 
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    } ${!isSelected && selectedIds.length >= 3 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-sm font-bold ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                        {report.assessmentTitle}
                      </span>
                      {isSelected ? (
                        <SafeIcon icon={FiCheck} className="text-blue-600" />
                      ) : (
                        <SafeIcon icon={FiPlus} className="text-gray-300" />
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {report.completedDate} • Score: {report.overallScore}%
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content: Comparison View */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {selectedIds.length === 0 ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-20 text-center"
                >
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiActivity} className="text-gray-400 text-3xl" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Select Assessments to Compare</h2>
                  <p className="text-gray-500 max-w-sm mx-auto">
                    Choose from your completed assessments on the left to start analyzing differences and trends.
                  </p>
                </motion.div>
              ) : (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-8"
                >
                  {/* Visual Comparison Card */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                      <SafeIcon icon={FiTrendingUp} className="mr-2 text-blue-600" /> Multi-Dimensional Performance
                    </h3>
                    <div className="h-[400px]">
                      <ComparisonRadarChart reports={selectedReports} />
                    </div>
                  </div>

                  {/* Score Grid Comparison */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {selectedReports.map((report) => (
                      <div key={report.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-6 border-b border-gray-100">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{report.completedDate}</span>
                            <div className={`text-xl font-bold ${getScoreColor(report.overallScore)}`}>
                              {report.overallScore}%
                            </div>
                          </div>
                          <h4 className="font-bold text-gray-900 line-clamp-1">{report.assessmentTitle}</h4>
                        </div>
                        <div className="p-6 space-y-4">
                          {report.sections.map((section, idx) => (
                            <div key={idx}>
                              <div className="flex justify-between text-xs font-medium mb-1">
                                <span className="text-gray-600">{section.title}</span>
                                <span className={getScoreColor(section.score)}>{section.score}%</span>
                              </div>
                              <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    section.score >= 80 ? 'bg-green-500' : section.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${section.score}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="p-4 bg-gray-50 border-t border-gray-100">
                          <Link 
                            to={`/report/${report.id}`}
                            className="text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center justify-center"
                          >
                            View Full Report <SafeIcon icon={FiArrowLeft} className="ml-1 rotate-180" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Comparison Table */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-200 bg-gray-50/50">
                      <h3 className="text-lg font-bold text-gray-900">Key Takeaways Comparison</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="p-6 text-sm font-bold text-gray-400 uppercase tracking-wider w-1/4">Assessment</th>
                            <th className="p-6 text-sm font-bold text-gray-400 uppercase tracking-wider">Executive Summary</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {selectedReports.map((report) => (
                            <tr key={report.id}>
                              <td className="p-6 align-top">
                                <div className="font-bold text-gray-900">{report.assessmentTitle}</div>
                                <div className="text-xs text-gray-500 mt-1">{report.completedDate}</div>
                              </td>
                              <td className="p-6 text-gray-600 text-sm italic leading-relaxed">
                                "{report.summary}"
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonTool;